import { Box, Snackbar } from "@mui/material";
import { toPng } from "html-to-image";
import PptxGenJS from "pptxgenjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RiSlideshowLine, RiFileList3Line } from "react-icons/ri";
import DescriptionIcon from "@mui/icons-material/Description";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import { useTranslation } from "react-i18next";
import { Company } from "../../db_access/company";
import OrganizationFootprintEnvBarChart from "../../environment-page-components/Footprint/OrganizationFootprintEnvBarChart";
import OrganizationFootprintEnvDoughnut from "../../environment-page-components/Footprint/OrganizationFootprintEnvDoughnut";
import { staticReferences } from "../../pages/Organization/OrganizationLifeCycleAssessment";
import { scope1Categories, scope2Categories, scope3CategoriesForSupplyChain } from "../../pages/Organization/ScopeCategory";
import useOrganizationHtmlReport from "../../hooks/useOrganizationHtmlReport";
import { HTML_REPORT_FEATURES } from "../../reports/reportConfig";
import { Banner, SplitButton } from "../m3";
import { addCoverPage, addExecutiveSummary, addFootprint, addInsights, addReferences, addScope1, addScope2, addScope3, addScopeSummaryTable } from "./ExportOrganizationPPT";
import { exportOrganizationReportAsWord } from "./ExportOrganizationWord";

const generatePpt = async (data) => {
  const pptx = new PptxGenJS();

  await addCoverPage(pptx, data);
  let num = 1;
  num = await addExecutiveSummary(pptx, data, num);
  num = await addScope1(pptx, data, num);
  num = await addScope2(pptx, data, num);
  num = await addScope3(pptx, data, num);
  num = await addFootprint(pptx, data, num);
  num = await addScopeSummaryTable(pptx, data, num);
  num = await addInsights(pptx, data, num);
  await addReferences(pptx, data, num);
  pptx.writeFile({ fileName: "Organization_Report_Template.pptx" });
};
const getScreenshot = async (ref) => {
  if (ref.current) {
    return await toPng(ref.current, { cacheBust: true, backgroundColor: "#ffffff" });
  }
  return null;
};
/**
 * @param {string} entityType - Structure.label or Supply_chain.label (enables the HTML report)
 * @param {object} entity - selected Structure / Supply Chain record
 */
const ExportOrganizationPPTButton = ({ emissions, ai_insight, emissionDetails, entityType, entity }) => {
  const { t } = useTranslation();
  const htmlReport = useOrganizationHtmlReport({ entityType, entity, emissions, emissionDetails });
  const product_env_doughnut_ref = useRef(null);
  const product_env_barchart_ref = useRef(null);
  const sankeyDiagramRef = useRef();
  const nomarlizedGWPChartRef = useRef();
  const [company, setCompany] = useState();
  const localEmissions = useMemo(() => (emissions ? emissions : []), [emissions]);
  const scope1 = useMemo(() => (localEmissions ? localEmissions.filter((e) => !e.is_avoided && scope1Categories.includes(e.category)) : []), [localEmissions]);
  const scope2 = useMemo(() => (localEmissions ? localEmissions.filter((e) => !e.is_avoided && scope2Categories.includes(e.category)) : []), [localEmissions]);
  const scope3 = useMemo(() => (localEmissions ? localEmissions.filter((e) => !e.is_avoided && scope3CategoriesForSupplyChain.includes(e.category)) : []), [localEmissions]);
  const scope1GWP = useMemo(() => scope1.reduce((total, e) => total + (e.gwp || 0), 0), [scope1]);
  const scope2GWP = useMemo(() => scope2.reduce((total, e) => total + (e.gwp || 0), 0), [scope2]);
  const scope3GWP = useMemo(() => scope3.reduce((total, e) => total + (e.gwp || 0), 0), [scope3]);
  const insights = useMemo(() => ai_insight?.insights || [], [ai_insight]);
  useEffect(() => {
    Company.get_mine()
      .then((res) => res.json())
      .then((data) => {
        setCompany(data.length > 0 ? data[0] : null);
      });
  }, []);
  const [imageUrl, setImageUrl] = useState(null);
  const handleScreenshot = async () => {
    if (nomarlizedGWPChartRef.current) {
      const dataUrl = await getScreenshot(nomarlizedGWPChartRef);
      setImageUrl(dataUrl);
    }
  };
  const generatePPT = useCallback(async () => {
    const footprintEnvDoughnut = await product_env_doughnut_ref.current?.getScreenshot();
    const footprintEnvBarchart = await product_env_barchart_ref.current?.getScreenshot();
    const sankeyDiagram = await sankeyDiagramRef.current?.getScreenshot();
    const objective = `Accounting corporate GHG emissions using Arivu to enable data-driven sustainability & net-zero planning`;
    const total_ghg_emission = `5616`;
    const organizational_boundaries = `Operational Control`;
    const base_year = `2025`;
    const reporting_standard = `GHG Protocol`;
    const references = staticReferences;

    const data = {
      footprintEnvDoughnut,
      footprintEnvBarchart,
      sankeyDiagram,
      insights,
      references,
      company,
      objective,
      total_ghg_emission,
      organizational_boundaries,
      base_year,
      reporting_standard,
      scope1,
      scope2,
      scope3,
      scope1GWP,
      scope2GWP,
      scope3GWP,
      emissions: localEmissions,
    };
    generatePpt(data);
  }, [company, insights, scope1, scope2, scope3, scope1GWP, scope2GWP, scope3GWP]);

  // BRSR PPT Generation
  const generateBRSRPPT = useCallback(async () => {
    try {

      const scope1Tonnes = scope1GWP / 1000;
      const scope2Tonnes = scope2GWP / 1000;
      const scope3Tonnes = scope3GWP / 1000;

      const organizationData = {
        name: company?.name || "N/A",
        year: new Date().getFullYear(),
        scope1: Math.round(scope1Tonnes * 100) / 100,
        prevScope1: 0,
        scope2: Math.round(scope2Tonnes * 100) / 100,
        prevScope2: 0,
        scope3: Math.round(scope3Tonnes * 100) / 100,
        prevScope3: 0,
        ghgIntensity: 0,
        scope3Intensity: 0,
        totalElectricity: 0,
        prevTotalElectricity: 0,
        totalFuel: 0,
        prevTotalFuel: 0,
        totalEnergy: 0,
        prevTotalEnergy: 0,
        energyIntensity: 0,
        prevEnergyIntensity: 0,
        patScheme: "Not applicable",
        surfaceWater: 0,
        prevSurfaceWater: 0,
        groundwater: 0,
        prevGroundwater: 0,
        thirdPartyWater: 0,
        prevThirdPartyWater: 0,
        waterConsumption: 0,
        prevWaterConsumption: 0,
        waterIntensity: 0,
        prevWaterIntensity: 0,
        zeroLiquidDischarge: "Not applicable",
        nox: 0,
        prevNox: 0,
        sox: 0,
        prevSox: 0,
        pm: 0,
        prevPm: 0,
        voc: 0,
        prevVoc: 0,
      };

      const {
        addBRSRTitleSlide,
        addBRSRSectionHeader,
        addBRSREnergySlide,
        addBRSRPATSlide,
        addBRSRWaterSlide,
        addBRSRZLDSlide,
        addBRSRAirEmissionsSlide,
        addBRSRGHGSlide,
        addBRSRGHGReductionSlide,
        addBRSRWasteSlide,
        addBRSRWastePracticesSlide,
        addBRSREcologicalSlide,
        addBRSREIASlide,
        addBRSRComplianceSlide,
        addBRSRRenewableEnergySlide,
        addBRSRWaterDischargeSlide,
        addBRSRWaterStressSlide,
        addBRSRScope3Slide,
        addBRSRBiodiversitySlide,
        addBRSRResourceInitiativesSlide,
        addBRSRDisasterManagementSlide,
        addBRSRValueChainSlide,
      } = await import("./ExportOrganizationPPT");

      const pptx = new PptxGenJS();
      let pageNum = 0;
      pageNum = await addBRSRTitleSlide(pptx, organizationData, pageNum);
      pageNum = 1;
      pageNum = await addBRSRSectionHeader(pptx, "I. Essential Indicators", pageNum);
      pageNum = await addBRSREnergySlide(pptx, organizationData, pageNum);
      pageNum = await addBRSRPATSlide(pptx, organizationData, pageNum);
      pageNum = await addBRSRWaterSlide(pptx, organizationData, pageNum);
      pageNum = await addBRSRZLDSlide(pptx, organizationData, pageNum);
      pageNum = await addBRSRAirEmissionsSlide(pptx, organizationData, pageNum);
      pageNum = await addBRSRGHGSlide(pptx, organizationData, pageNum);
      pageNum = await addBRSRGHGReductionSlide(pptx, organizationData, pageNum);
      pageNum = await addBRSRWasteSlide(pptx, organizationData, pageNum);
      pageNum = await addBRSRWastePracticesSlide(pptx, organizationData, pageNum);
      pageNum = await addBRSREcologicalSlide(pptx, organizationData, pageNum);
      pageNum = await addBRSREIASlide(pptx, organizationData, pageNum);
      pageNum = await addBRSRComplianceSlide(pptx, organizationData, pageNum);
      pageNum = await addBRSRSectionHeader(pptx, "II. Leadership Indicators", pageNum);
      pageNum = await addBRSRRenewableEnergySlide(pptx, organizationData, pageNum);
      pageNum = await addBRSRWaterDischargeSlide(pptx, organizationData, pageNum);
      pageNum = await addBRSRWaterStressSlide(pptx, organizationData, pageNum);
      pageNum = await addBRSRScope3Slide(pptx, organizationData, pageNum);
      pageNum = await addBRSRBiodiversitySlide(pptx, organizationData, pageNum);
      pageNum = await addBRSRResourceInitiativesSlide(pptx, organizationData, pageNum);
      pageNum = await addBRSRDisasterManagementSlide(pptx, organizationData, pageNum);
      pageNum = await addBRSRValueChainSlide(pptx, organizationData, pageNum);

      const fileName = `Organization_BRSR_Report_${organizationData.name}_${organizationData.year}.pptx`;
      await pptx.writeFile({ fileName });
      alert("BRSR PPT report generated successfully!");
    } catch (error) {
      console.error("Error generating BRSR PPT:", error);
      alert("An error occurred while generating the BRSR PPT report.");
    }
  }, [company, scope1GWP, scope2GWP, scope3GWP]);

  // BRSR Word Generation
  const generateBRSRWord = useCallback(async () => {
    try {
      const scope1Tonnes = scope1GWP / 1000;
      const scope2Tonnes = scope2GWP / 1000;
      const scope3Tonnes = scope3GWP / 1000;

      const organizationData = {
        name: company?.name || "N/A",
        year: new Date().getFullYear(),
        scope1: Math.round(scope1Tonnes * 100) / 100,
        prevScope1: 0,
        scope2: Math.round(scope2Tonnes * 100) / 100,
        prevScope2: 0,
        scope3: Math.round(scope3Tonnes * 100) / 100,
        prevScope3: 0,
        company,
      };

      await exportOrganizationReportAsWord(organizationData);
      alert("BRSR Word report generated successfully!");
    } catch (error) {
      console.error("Error generating BRSR Word:", error);
      alert("An error occurred while generating the BRSR Word report.");
    }
  }, [company, scope1GWP, scope2GWP, scope3GWP]);

  const reportOptions = useMemo(
    () => [
      {
        key: "html-report",
        label: t("Interactive HTML report"),
        description: htmlReport.isGenerating
          ? t("Generating report…")
          : HTML_REPORT_FEATURES.brsr
            ? t("Offline report with interactive charts, filters and BRSR Principle 6")
            : t("Offline report with interactive charts and filters"),
        icon: <InsightsOutlinedIcon sx={{ color: "var(--m3-primary)", fontSize: 18 }} />,
        onClick: htmlReport.generate,
        disabled: htmlReport.isGenerating,
        hidden: !entityType,
      },
      {
        key: "corporate-ghg",
        label: "Corporate GHG PPT",
        description: "Standard presentation deck with footprint & methodology",
        icon: <RiSlideshowLine size={18} style={{ color: "var(--m3-primary)" }} />,
        onClick: generatePPT,
      },
      {
        key: "brsr-ppt",
        label: "BRSR PPT",
        description: "SEBI BRSR compliant sustainability report presentation",
        icon: <RiFileList3Line size={18} style={{ color: "var(--m3-primary)" }} />,
        onClick: generateBRSRPPT,
      },
      {
        key: "brsr-word",
        label: "BRSR Word",
        description: "Comprehensive editable documentation format (.docx)",
        icon: <DescriptionIcon sx={{ color: "var(--m3-primary)", fontSize: 18 }} />,
        onClick: generateBRSRWord,
      },
    ],
    [generatePPT, generateBRSRPPT, generateBRSRWord, htmlReport.generate, htmlReport.isGenerating, entityType, t]
  );

  return (
    <Box>
      <Box
        sx={{
          position: "absolute",
          left: "-9999px",
          top: 0,
        }}>
        <button onClick={handleScreenshot} style={{ marginTop: 10 }}>
          生成截图
        </button>

        {imageUrl && (
          <div style={{ marginTop: 10 }}>
            <p>截图预览：</p>
            <img src={imageUrl} alt="Screenshot preview" style={{ border: "1px solid #ccc" }} />
          </div>
        )}
        <Box sx={{ width: 800 }}>
          <OrganizationFootprintEnvDoughnut emissions={localEmissions} ref={product_env_doughnut_ref} />
        </Box>
        <Box sx={{ width: 800 }}>
          <OrganizationFootprintEnvBarChart emissions={localEmissions} ref={product_env_barchart_ref} />
        </Box>
      </Box>

      <SplitButton
        label="Generate Report"
        icon={<RiSlideshowLine size={18} />}
        onClick={generatePPT}
        options={reportOptions}
        variant="filled"
        size="md"
      />
      <Snackbar open={Boolean(htmlReport.error)} autoHideDuration={6000} onClose={htmlReport.clearError} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <div>
          <Banner tone="error" onDismiss={htmlReport.clearError}>
            {htmlReport.error}
          </Banner>
        </div>
      </Snackbar>
    </Box>
  );
};

export default ExportOrganizationPPTButton;
